export type ComposerFollowUpOption = {
	id: string;
	label: string;
	prompt: string;
	ordinal?: number;
};

type ComposerFollowUpVisibility = {
	optionCount: number;
	reasonedApprovalActive: boolean;
	reasonedQuestionCount: number;
	onboardingMemoryConfirmationVisible: boolean;
	contentFactoryTopicVisible: boolean;
};

export const shouldShowComposerFollowUpOptions = ({
	optionCount,
	reasonedApprovalActive,
	reasonedQuestionCount,
	onboardingMemoryConfirmationVisible,
	contentFactoryTopicVisible
}: ComposerFollowUpVisibility) =>
	optionCount > 0 &&
	!reasonedApprovalActive &&
	reasonedQuestionCount === 0 &&
	!onboardingMemoryConfirmationVisible &&
	!contentFactoryTopicVisible;

type ChatMessageLike = {
	parentId?: string | null;
	suppressContextualFollowUps?: boolean;
} | null;

export const shouldSuppressContextualFollowUps = (
	assistantMessage: ChatMessageLike,
	messages: Record<string, ChatMessageLike> = {}
) => {
	const parentId = assistantMessage?.parentId;
	if (!parentId) return false;

	return messages[parentId]?.suppressContextualFollowUps === true;
};

const normalizeFollowUp = (value: unknown) =>
	String(value ?? '')
		.replace(/^\s*(?:[-*•]|\d+[.)])\s*/, '')
		.replace(/\s+/g, ' ')
		.trim();

const compactFollowUpLabel = (prompt: string, maxLength: number) => {
	if (prompt.length <= maxLength) return prompt;

	const sentence = prompt.split(/(?<=[.!?])\s+/)[0]?.trim() ?? prompt;
	const source = sentence.length <= maxLength ? sentence : prompt;
	const candidate = source.slice(0, maxLength + 1);
	const lastWhitespace = candidate.lastIndexOf(' ');
	const boundary = lastWhitespace >= Math.floor(maxLength * 0.58) ? lastWhitespace : maxLength;

	return `${source.slice(0, boundary).trimEnd()}…`;
};

export const buildComposerFollowUpOptions = (
	followUps: unknown,
	{ maxItems = 5, maxLabelLength = 48 }: { maxItems?: number; maxLabelLength?: number } = {}
): ComposerFollowUpOption[] => {
	if (!Array.isArray(followUps)) return [];

	const seen = new Set<string>();
	const options: ComposerFollowUpOption[] = [];

	for (const value of followUps) {
		const prompt = normalizeFollowUp(value);
		const key = prompt.toLocaleLowerCase();
		if (!prompt || seen.has(key)) continue;

		seen.add(key);
		options.push({
			id: `follow-up-${options.length + 1}`,
			label: compactFollowUpLabel(prompt, Math.max(18, maxLabelLength)),
			prompt
		});

		if (options.length >= Math.max(1, maxItems)) break;
	}

	return options;
};
